"use client";
import { DatePickerProvider } from "@/app/components/common/DatePicker/DatePickerContext";
import HeroHeader from "@/app/components/common/HeroHeader";
import Search from "@/app/components/common/Search";
import SearchFlightsWidget from "@/app/components/common/SearchFlightsWidget/SearchFlightsWidget";
import { Switch } from "@/app/components/common/Switch/Switch";
import SVG from "@/app/components/pages/SVG";
import { Destination } from "@/types";
import { useRef, useState } from "react";
import LogoLists from "./LogoLists";
import { useTranslations } from "next-intl";
import TopBanner from "@/app/components/pages/Home/TopBanner";
import StayDiscountBanner from "@/app/components/pages/Home/StayDiscountBanner";
import type { StayRule } from "@/app/api/utils/getHighestDiscount";

type Props = {
  type: "hotels" | "flights";
  staticContentData?: {
    banner_image?: string;
    banner_heading?: string;
    banner_description?: string;
    logo?: string;
  };
  stayRule?: Pick<StayRule, "percentage" | "minCheckIn"> | null;
};

const SearchFlightsHero = ({
  type,
  staticContentData = {},
  stayRule = null,
}: Props) => {
  const calendarRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const [destination, setDestination] = useState<Destination>({});
  const t = useTranslations("HomePage");

  const defaultBg =
    type === "flights"
      ? "/assets/v2/images/sleap-hero-flights.jpg"
      : "/assets/v2/images/sleap-hero-hotels.jpg";
  const bgUrl = staticContentData.banner_image ?? defaultBg;

  const defaultHeading =
    type === "flights" ? t("defaultFlightsHeading") : t("defaultHotelsHeading");
  const headingText = staticContentData.banner_heading ?? defaultHeading;

  const defaultDesc = t("unlockExclusiveWeb3");
  const description = staticContentData.banner_description ?? defaultDesc;

  return (
    <div>
      {stayRule ? (
        <StayDiscountBanner stayRule={stayRule} searchRef={searchRef} />
      ) : (
        <TopBanner />
      )}
      <div
        className="bg-cover bg-center w-full"
        style={{ backgroundImage: `url(${bgUrl})` }}
      >
        <div className="container max-w-7xl mx-auto">
          <HeroHeader logo={staticContentData?.logo || ""} />

          <div className="flex flex-col-reverse md:flex-col">
            <div className="flex justify-center mb-3 md:mb-11">
              <Switch type={type} t={t} />
            </div>

            <div className="text-center text-white text-3xl lg:text-5xl font-semibold font-inter mb-9 md:mb-16">
              <span dangerouslySetInnerHTML={{ __html: headingText }} />

              {!staticContentData.banner_heading && type === "hotels" && (
                <span className="block relative md:inline-block">
                  &nbsp;&{" "}
                  {t.rich("saveUpTo", {
                    span: (chunk) => <span>{chunk}</span>,
                  })}
                  <div className="absolute top-[80%] left-1/2 -translate-x-1/2 h-auto max-w-48 md:max-w-full w-full">
                    <SVG
                      alt=""
                      src="/assets/v2/icons/wave.svg"
                      twClasses="block"
                      width={239}
                      height={35}
                    />
                  </div>
                </span>
              )}
            </div>
          </div>

          {type === "hotels" ? (
            <>
              <div className="home-page-banner">
                <DatePickerProvider>
                  <Search
                    searchRef={searchRef}
                    calendarRef={calendarRef}
                    setDestination={setDestination}
                    destination={destination}
                    isHomePage={true}
                  />
                </DatePickerProvider>
              </div>
              <p className="text-xl font-medium text-white text-center m-0 mt-8">
                {description}
              </p>
            </>
          ) : (
            <SearchFlightsWidget />
          )}

          <LogoLists />
        </div>
      </div>
    </div>
  );
};

export default SearchFlightsHero;
